import countryPhoneRules from './countryPhoneRules.json';

export type PhoneValidationRule = {
  minLength?: number;
  maxLength?: number;
  allowedLengths?: number[];
  startsWith?: string[];
};

const COUNTRY_DIAL_CODES: Record<string, string> = {
  ad: '376',
  ae: '971',
  af: '93',
  al: '355',
  ar: '54',
  at: '43',
  au: '61',
  az: '994',
  ba: '387',
  be: '32',
  bg: '359',
  bh: '973',
  br: '55',
  ca: '1',
  ch: '41',
  cn: '86',
  cz: '420',
  de: '49',
  dk: '45',
  es: '34',
  fi: '358',
  fr: '33',
  gb: '44',
  gr: '30',
  hk: '852',
  ie: '353',
  il: '972',
  in: '91',
  ir: '98',
  it: '39',
  jp: '81',
  kr: '82',
  lb: '961',
  mx: '52',
  my: '60',
  nl: '31',
  no: '47',
  nz: '64',
  ph: '63',
  pk: '92',
  pl: '48',
  pt: '351',
  ro: '40',
  rs: '381',
  sa: '966',
  se: '46',
  sg: '65',
  si: '386',
  sk: '421',
  th: '66',
  tr: '90',
  tw: '886',
  ua: '380',
  uk: '44',
  us: '1',
  vn: '84',
  za: '27',
};

const { getCode } = require('country-list');

const COUNTRY_NAME_ALIASES: Record<string, string> = {
  iran: 'ir',
  'islamic republic of iran': 'ir',
  'iran, islamic republic of': 'ir',
  'republic of iran': 'ir',
  'united states': 'us',
  usa: 'us',
  'united kingdom': 'gb',
  uk: 'gb',
  'great britain': 'gb',
};

export function resolveCountryCode(countryName: string): string {
  const normalized = (countryName || '').trim().toLowerCase();
  const alias = COUNTRY_NAME_ALIASES[normalized];

  if (alias) {
    return alias;
  }

  try {
    const countryCode = getCode(countryName || '');
    return (countryCode || '').toLowerCase();
  } catch {
    return '';
  }
}

const COUNTRY_PHONE_RULES: Record<string, PhoneValidationRule> = Object.fromEntries(
  countryPhoneRules.map((countryRule) => {
    const countryCode = resolveCountryCode(countryRule.country) || (getCode(countryRule.country) || '').toLowerCase();
    const lengths = Array.from(new Set(countryRule.length.map((value) => Number(value)))).sort((a, b) => a - b);

    return [
      countryCode,
      {
        minLength: lengths[0],
        maxLength: lengths[lengths.length - 1],
        allowedLengths: lengths,
        startsWith: countryRule.startsWith,
      },
    ];
  })
);

COUNTRY_PHONE_RULES.default = {
  minLength: 7,
  maxLength: 15,
  allowedLengths: Array.from({ length: 9 }, (_, index) => index + 7),
};

export function normalizePhoneNumber(phone: string): string {
  if (!phone) {
    return '';
  }

  return phone
    .replace(/\s+/g, '')
    .replace(/[()\-]/g, '')
    .replace(/^00/, '')
    .replace(/^\+/, '')
    .replace(/[^\d]/g, '');
}

export function isPhoneNumberValid(phone: string, countryCode?: string): boolean {
  const normalizedCountryCode = (countryCode || '').toLowerCase();
  const sanitized = normalizePhoneNumber(phone);

  if (!sanitized) {
    return false;
  }

  const rule = COUNTRY_PHONE_RULES[normalizedCountryCode] || COUNTRY_PHONE_RULES.default;
  const dialCode = COUNTRY_DIAL_CODES[normalizedCountryCode] || '';
  let candidate = sanitized;

  if (dialCode && candidate.startsWith(dialCode)) {
    candidate = candidate.slice(dialCode.length);
  }

  if (normalizedCountryCode === 'ir' && candidate.startsWith('0')) {
    candidate = candidate.slice(1);
  }

  if (!/^\d+$/.test(candidate)) {
    return false;
  }

  const allowedLengths = rule.allowedLengths && rule.allowedLengths.length ? rule.allowedLengths : [];
  const matchesLength = allowedLengths.length > 0 ? allowedLengths.includes(candidate.length) : false;
  const matchesRange =
    typeof rule.minLength === 'number' && typeof rule.maxLength === 'number'
      ? candidate.length >= rule.minLength && candidate.length <= rule.maxLength
      : false;

  if (rule.startsWith && !rule.startsWith.some((prefix) => candidate.startsWith(prefix))) {
    return false;
  }

  if (allowedLengths.length > 0) {
    return matchesLength;
  }

  return matchesRange;
}
